import React, { useEffect, useState, useCallback } from "react";
import api from "../services/api";
import { useParams } from "react-router-dom";
import "./BalancePage.css";

const BalancePage = () => {
    const { id: businessId } = useParams();
    const [transactions, setTransactions] = useState([]);
    const [categories, setCategories] = useState([]);
    const [searchKeyword, setSearchKeyword] = useState("");
    const [filterCategory, setFilterCategory] = useState("Uncategorized");
    const [selectedTransactions, setSelectedTransactions] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [showSelectAll, setShowSelectAll] = useState(false);
    const [newCategory, setNewCategory] = useState("");
    const [categoryType, setCategoryType] = useState("General");
    const [showCategoryPopup, setShowCategoryPopup] = useState(false);
    const [showAddTransactionPopup, setShowAddTransactionPopup] = useState(false);
    const [showEditPopup, setShowEditPopup] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState("");
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [newTransaction, setNewTransaction] = useState({
        date: "",
        amount: "",
        description: "",
        category: "",
    });
    const [filteredTransactions, setFilteredTransactions] = useState([]);

    // Fetch Transactions and Categories


    const fetchTransactions = useCallback(async () => {
        try {
            const response = await api.get(`/transactions/${businessId}`);
            setTransactions(response.data);
        } catch (error) {
            console.error("Error fetching transactions:", error);
        }
    }, [businessId]);

    const fetchCategories = useCallback(async () => {
        try {
            const response = await api.get(`/categories`);
            setCategories(response.data);
        } catch (error) {
            console.error("Error fetching categories:", error);
        }
    }, []);

    // ✅ Now no ESLint warning
    useEffect(() => {
        fetchTransactions();
        fetchCategories();
    }, [fetchTransactions, fetchCategories]);
    




    const handleSelectTransaction = (id) => {
        setSelectedTransactions((prev) =>
            prev.includes(id) ? prev.filter((txnId) => txnId !== id) : [...prev, id]
        );
    };

    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedTransactions([]);
        } else {
            setSelectedTransactions(filteredTransactions.map((txn) => txn.id));
        }
        setSelectAll(!selectAll);
    };

    const handleAddTransaction = async () => {
        try {
            let amount = parseFloat(newTransaction.amount);
            const selectedCategoryDetails = categories.find(cat => cat.name === newTransaction.category);

            if (selectedCategoryDetails?.category_type === "Expense") {
                amount = -Math.abs(amount);
            }

            await api.post("/transactions/add", {
                user_id: 1,
                business_id: businessId,
                date: newTransaction.date,
                amount,
                description: newTransaction.description,
                category: newTransaction.category,
                category_type: newTransaction.category_type
            });

            alert("Transaction added successfully!");
            refreshData();
            setShowAddTransactionPopup(false);
            setNewTransaction({ date: "", amount: "", description: "", category: "" });
        } catch (error) {
            console.error("Error adding transaction:", error);
            alert("Failed to add transaction.");
        }
    };

    const handleDeleteSelected = async () => {
        try {
            await api.delete("/transactions/delete", {
                data: { transactionIds: selectedTransactions },
            });
            alert("Transactions deleted successfully!");
            refreshData();
        } catch (error) {
            console.error("Error deleting transactions:", error);
            alert("Failed to delete transactions.");
        }
    };


    const handleEditClick = (txn) => {
        setSelectedTransaction({ ...txn }); // Open the edit popup with transaction data
    };

    const handleEditChange = (field, value) => {
        setSelectedTransaction((prev) => ({ ...prev, [field]: value }));
    };


    const handleEditSubmit = async () => {
        try {
            const updatedTxn = { ...selectedTransaction };
            const categoryDetails = categories.find(cat => cat.name === updatedTxn.category);

            const adjustedAmount = categoryDetails?.category_type === "Expense"
                ? -Math.abs(updatedTxn.amount)
                : Math.abs(updatedTxn.amount);

            await api.put("/transactions/update-manual", {
                id: updatedTxn.id,
                date: updatedTxn.date,
                amount: adjustedAmount,
                description: updatedTxn.description,
                category: updatedTxn.category,
                category_type: updatedTxn.category_type
            });

            alert("Transaction updated successfully!");
            setSelectedTransaction(null);
            fetchTransactions();
        } catch (error) {
            console.error("Error updating transaction:", error);
            alert("Failed to update transaction.");
        }
    };

    
    

    const handleEditSelected = async () => {
        try {
            // Gather all selected transactions
            const updatedTransactions = selectedTransactions.map((txnId) =>
                transactions.find((txn) => txn.id === txnId)
            );
    
    
            // Update categories for auto-generated transactions
            if (updatedTransactions.length > 0) {
                await api.put("/transactions/update-categories", {
                    transactionIds: updatedTransactions.map(txn => txn.id),
                    category: selectedCategory
                });
            }
    
            alert("Transactions updated successfully!");
            setShowEditPopup(false);
            refreshData();
        } catch (error) {
            console.error("Error updating transactions:", error);
            alert("Failed to update transactions.");
        }
    };
    

    const handleCategorySubmit = async (addAnother = false) => {
        if (newCategory.trim() === "") {
            alert("Category name cannot be empty.");
            return;
        }

        try {
            await api.post("/categories", { name: newCategory, category_type: categoryType });
            alert("Category added successfully!");
            fetchCategories();
            if (!addAnother) setShowCategoryPopup(false);
            setNewCategory("");
            setCategoryType("General");
        } catch (error) {
            console.error("Error adding category:", error);
            alert("Failed to add category.");
        }
    };

    // const handleSearch = () => {
    //     const filtered = transactions.filter((txn) => {
    //         const keywordMatch = txn.description.toLowerCase().includes(searchKeyword.toLowerCase());
    //         return keywordMatch;
    //     });
    //     setFilteredTransactions(filtered);
    //     setShowSelectAll(true);
    // };

    const handleSearch = () => {
        const keyword = searchKeyword.trim().toLowerCase();
        const isNumber = !isNaN(keyword) && keyword !== "";
    
        const filtered = transactions.filter((txn) => {
            if (isNumber) {
                // Search for amount matches
                return parseFloat(txn.amount) === parseFloat(keyword);
            } else {
                // Search for description matches
                return txn.description.toLowerCase().includes(keyword);
            }
        });
    
        setFilteredTransactions(filtered);
        setShowSelectAll(true);
    };
    

    const handleShowAll = () => {
        setFilterCategory("");
        setSearchKeyword("");
        setShowSelectAll(false);
        setFilteredTransactions(transactions);
    };

    const refreshData = () => {
        setSelectedTransactions([]);
        setSelectAll(false);
        fetchTransactions();
        handleShowAll();
    };

    return (
        <div className="bal-container">
            <h1 className="bal-title">Balance Page</h1>

            <div className="bal-controls">
                <input
                    type="text"
                    placeholder="Search by description"
                    value={searchKeyword}
                    onChange={(e) => setSearchKeyword(e.target.value)}
                    className="bal-search-input"
                />

                <select
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                    className="bal-category-filter"
                >
                    <option value="">select category</option>
                    {categories.map((category) => (
                        <option key={category.id} value={category.name}>
                            {category.name}
                        </option>
                    ))}
                </select>

                <button onClick={handleSearch} className="bal-search-btn">Search</button>
                <button onClick={handleShowAll} className="bal-all-btn">All Transactions</button>
                <button onClick={() => setShowAddTransactionPopup(true)} className="bal-add-btn">Add Transaction</button>
                <button onClick={handleDeleteSelected} className="bal-delete-btn" disabled={selectedTransactions.length === 0}>
                    Delete Selected
                </button>
                <button onClick={() => setShowEditPopup(true)} className="bal-edit-btn" disabled={selectedTransactions.length === 0}>
                    Edit Selected
                </button>
                <button onClick={() => setShowCategoryPopup(true)} className="bal-category-btn">Add Category</button>
            </div>

            {showEditPopup && (
    <>
        <div className="bal-overlay" onClick={() => setShowEditPopup(false)}></div>
        <div className="bal-edit-popup">
            <h3>Edit Selected Transactions</h3>
            <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="bal-category-select"
            >
                <option value="">Select Category</option>
                {categories.map((category) => (
                    <option key={category.id} value={category.name}>
                        {category.name}
                    </option>
                ))}
            </select>
            <button onClick={handleEditSelected} className="bal-submit-btn">Submit</button>
            <button onClick={() => setShowEditPopup(false)} className="bal-cancel-btn">Cancel</button>
        </div>
    </>
)}



        
{showCategoryPopup && (
    <>
        <div className="bal-category-overlay" onClick={() => setShowCategoryPopup(false)}></div>
        <div className="bal-category-popup">
            <h3>Add New Category</h3>
            <input
                type="text"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                placeholder="Enter category name"
                className="bal-category-input"
            />
            <select
                value={categoryType}
                onChange={(e) => setCategoryType(e.target.value)}
                className="bal-category-type-select"
            >
                <option value="">Select Category Type</option>
                <option value="Income">Income</option>
                <option value="Expense">Expense</option>
                
            </select>
            <button onClick={() => handleCategorySubmit(false)} className="bal-category-submit-btn">Submit</button>
            <button onClick={() => handleCategorySubmit(true)} className="bal-category-submit-another-btn">Submit & Add Another</button>
            <button onClick={() => setShowCategoryPopup(false)} className="bal-category-cancel-btn">Cancel</button>
        </div>
    </>
)}

          

        {showAddTransactionPopup && (
    <div className="add-transaction-popup">
        <h3>Add New Transaction</h3>

        <input
            type="date"
            value={newTransaction.date}
            onChange={(e) =>
                setNewTransaction({ ...newTransaction, date: e.target.value })
            }
            className="add-transaction-input"
            placeholder="Date"
        />

        <input
            type="number"
            placeholder="Amount"
            value={newTransaction.amount}
            onChange={(e) =>
                setNewTransaction({ ...newTransaction, amount: e.target.value })
            }
            className="add-transaction-input"
        />

        <input
            type="text"
            placeholder="Description"
            value={newTransaction.description}
            onChange={(e) =>
                setNewTransaction({ ...newTransaction, description: e.target.value })
            }
            className="add-transaction-input"
        />

        {/* Category Dropdown */}
        <select
            value={newTransaction.category}
            onChange={(e) =>
                setNewTransaction({ ...newTransaction, category: e.target.value })
            }
            className="add-transaction-select"
        >
            <option value="">Select Category</option>
            {categories.map((category) => (
                <option key={category.id} value={category.name}>
                    {category.name}
                </option>
            ))}
        </select>

        {/* Category Type Dropdown */}
        <select
            value={newTransaction.category_type || ""}
            onChange={(e) =>
                setNewTransaction({ ...newTransaction, category_type: e.target.value })
            }
            className="add-transaction-select"
        >
            <option value="">Select Category Type</option>
            <option value="Expense">Expense</option>
            <option value="Income">Income</option>
        </select>

        <button onClick={handleAddTransaction} className="add-transaction-submit-btn">
            Submit
        </button>
        <button
            onClick={() => setShowAddTransactionPopup(false)}
            className="add-transaction-cancel-btn"
        >
            Cancel
        </button>
    </div>
)}



            <table className="bal-table">
                <thead>
                    <tr>
                    {showSelectAll ? (
            <th>
                <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAll}
                />
                            </th>
                        ) : (
                            <th></th>  
                        )}
                        
                        <th>Date</th>
                        <th>Description</th>
                        <th>Category</th>
                        <th>Amount</th>
                    </tr>
                </thead>
                <tbody>
                    {(showSelectAll ? filteredTransactions : transactions).map((txn) => (
                        <tr key={txn.id}>
                            <td>
                                <input
                                    type="checkbox"
                                    checked={selectedTransactions.includes(txn.id)}
                                    onChange={() => handleSelectTransaction(txn.id)}
                                />
                            </td>
                            <td>{new Date(txn.date).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "short",
                                day: "2-digit"
                            })}
                            </td>
                            <td>{txn.description}</td>
                            <td>{txn.category}</td>
                            <td>
                            {txn.amount !== undefined && txn.amount !== null
                                ? (txn.amount < 0
                                    ? `-$${Math.abs(txn.amount).toFixed(2)}`
                                    : `$${txn.amount.toFixed(2)}`
                                )
                                : "$0.00"}
                            </td>
                            <td>
                                {txn.manually_added === 1 && (
                                    <button className="edit-icon" onClick={() => handleEditClick(txn)}>✏️</button>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {selectedTransaction && (
    <div className="transaction-edit-popup">
        <h3>Edit Transaction</h3>

        <input
            type="date"
            value={selectedTransaction.date.split("T")[0]}
            onChange={(e) => handleEditChange("date", e.target.value)}
            className="transaction-edit-input"
        />

        <input
            type="number"
            value={Math.abs(selectedTransaction.amount)}
            onChange={(e) => handleEditChange("amount", parseFloat(e.target.value))}
            className="transaction-edit-input"
        />

        <input
            type="text"
            value={selectedTransaction.description}
            onChange={(e) => handleEditChange("description", e.target.value)}
            className="transaction-edit-input"
        />

        <select
            value={selectedTransaction.category}
            onChange={(e) => handleEditChange("category", e.target.value)}
            className="transaction-edit-select"
        >
            <option value="">Select Category</option>
            {categories.map((category) => (
                <option key={category.id} value={category.name}>
                    {category.name} 
                </option>
            ))}
        </select>

        <select
            value={selectedTransaction.category_type || ""}
            onChange={(e) => handleEditChange("category_type", e.target.value)}
            className="transaction-edit-select"
        >
            <option value="">Select Category Type</option>
            <option value="Expense">Expense</option>
            <option value="Income">Income</option>
        </select>

        <button
            onClick={handleEditSubmit}
            className="transaction-edit-btn"
        >
            Submit
        </button>

        <button
            onClick={() => setSelectedTransaction(null)}
            className="transaction-edit-cancel-btn"
        >
            Cancel
        </button>
    </div>
)}


        </div>
    );
};

export default BalancePage;





